import { useEffect, useMemo, useState } from "react";
import { companyKpiUseCase } from "../../../../di/container";
import {
  allowedIntervals,
  presetRange,
  graphSeriesLabel,
  isManualMetric,
  metricOption,
  type ComputeResponse,
  type GoalDirection,
  type KpiChartType,
  type KpiInterval,
  type KpiMachineScope,
  type KpiPeriodPreset,
  type MesDashboardCard,
} from "../../../../domain/types/company/company-kpi";
import { ComputeChart } from "./ComputeChart";
import { WorkloadLogModal } from "./WorkloadLogModal";

const INTERVALS: { v: KpiInterval; l: string }[] = [
  { v: "MINUTE", l: "분" },
  { v: "HOUR", l: "시간" },
  { v: "DAY", l: "일" },
  { v: "WEEK", l: "주" },
  { v: "MONTH", l: "월" },
];
const PERIODS: { v: KpiPeriodPreset; l: string }[] = [
  { v: "1D", l: "오늘" },
  { v: "7D", l: "7일" },
  { v: "30D", l: "30일" },
  { v: "3M", l: "3개월" },
  { v: "6M", l: "6개월" },
  { v: "12M", l: "12개월" },
];


/** 도입 전→목표 달성률(%). 방향 반영. 값 부족 시 null. */
function achievement(
  base: number | null,
  goal: number | null,
  actual: number | null,
  direction: GoalDirection,
): number | null {
  if (base == null || goal == null || actual == null) return null;
  const denom = direction === "DECREASE" ? base - goal : goal - base;
  if (denom === 0) return null;
  const numer = direction === "DECREASE" ? base - actual : actual - base;
  return Math.max(0, (numer / denom) * 100);
}

const fmt = (n: number | null, unit: string) =>
  n == null ? "—" : `${Number(n.toFixed(2))}${unit}`;

const ChipGroup = <T extends string>({
  options,
  value,
  onChange,
  equalWidth,
  isDisabled,
}: {
  options: { v: T; l: string }[];
  value: T;
  onChange: (v: T) => void;
  /** 모든 탭 폭 동일(짧은/긴 라벨 정렬용). */
  equalWidth?: boolean;
  /** 선택 불가 옵션 판정(회색·비활성). */
  isDisabled?: (v: T) => boolean;
}) => (
  <div className="inline-flex items-center rounded-[6px] border border-[#e0e0e0] overflow-hidden">
    {options.map((o) => {
      const disabled = isDisabled?.(o.v) ?? false;
      return (
        <button
          key={o.v}
          onClick={() => !disabled && onChange(o.v)}
          disabled={disabled}
          title={disabled ? "선택한 조회 기간에서는 이 단위가 너무 촘촘해 사용할 수 없습니다" : undefined}
          className={`px-2 py-[5px] text-[11px] border-r border-[#e0e0e0] last:border-r-0 transition-colors ${
            equalWidth ? "min-w-[36px] text-center" : ""
          } ${
            disabled
              ? "bg-[#FAFAFA] text-[#CCC] cursor-not-allowed"
              : value === o.v
                ? "bg-[#4285F4] text-white"
                : "bg-white text-[#666] hover:bg-[#F5F5F5]"
          }`}
        >
          {o.l}
        </button>
      );
    })}
  </div>
);

interface Props {
  card: MesDashboardCard;
  onEdit: (card: MesDashboardCard) => void;
  onDelete: (card: MesDashboardCard) => void;
}

/**
 * 회사 KPI 벤치마크 카드.
 * - 계산형 지표: /compute 그래프 + 실측(기간 평균) + 현재값/목표/달성률.
 * - 수동형 지표: 회사 입력 실측값 + 현재값/목표/달성률 (그래프 없음).
 */
export const KpiCard = ({ card, onEdit, onDelete }: Props) => {
  const metric = card.metrics[0]; // 회사 KPI = 카드당 지표 1개
  const opt = metric ? metricOption(metric.metricType) : undefined;
  const unit = opt?.unit ?? "";
  // 집계 방식: SUM(생산량·설계변경=조회 기간 합), AVG(가동률·리드타임·납기준수율=평균).
  const aggregate = opt?.aggregate ?? "AVG";
  const manual = metric ? isManualMetric(metric.metricType) : false;
  const direction = (metric?.goalDirection as GoalDirection) ?? "INCREASE";
  // 드릴다운 — 그래프 점 클릭 시 그 날 워크로드 로그(카드 선택 장비만 필터).
  const cardMachineIds =
    metric?.machineScope === "SPECIFIC" ? metric.machineIds : undefined;
  const [drilldown, setDrilldown] = useState<string | null>(null);

  const [interval, setInterval] = useState<KpiInterval>(card.interval as KpiInterval);
  const [periodPreset, setPeriodPreset] = useState<KpiPeriodPreset>(
    card.periodPreset as KpiPeriodPreset,
  );
  const [chartType, setChartType] = useState<KpiChartType>(card.chartType as KpiChartType);
  const [customStart, setCustomStart] = useState(card.customStart ?? "");
  const [customEnd, setCustomEnd] = useState(card.customEnd ?? "");

  const [data, setData] = useState<ComputeResponse | null>(null);
  const [loading, setLoading] = useState(!manual);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const p = card.periodPreset as KpiPeriodPreset;
    setInterval(card.interval as KpiInterval);
    setPeriodPreset(p);
    setChartType(card.chartType as KpiChartType);
    // 날짜창은 항상 노출 — 저장된 날짜가 있으면 사용, 없으면(프리셋 카드) 프리셋 범위로 채움.
    if (card.customStart && card.customEnd) {
      setCustomStart(card.customStart);
      setCustomEnd(card.customEnd);
    } else {
      const r = presetRange(p);
      setCustomStart(r?.start ?? "");
      setCustomEnd(r?.end ?? "");
    }
  }, [card.interval, card.periodPreset, card.chartType, card.customStart, card.customEnd]);

  // 프리셋 클릭 → 하이라이트 + 날짜창을 그 범위로 자동 채움(편집 가능). 조회는 상대(periodPreset) 기준.
  const selectPreset = (p: KpiPeriodPreset) => {
    setPeriodPreset(p);
    const r = presetRange(p);
    if (r) {
      setCustomStart(r.start);
      setCustomEnd(r.end);
    }
  };
  // 날짜 직접 수정 → CUSTOM 전환(프리셋 하이라이트 해제) + 고정 날짜 조회.
  const editStart = (v: string) => {
    setCustomStart(v);
    setPeriodPreset("CUSTOM");
  };
  const editEnd = (v: string) => {
    setCustomEnd(v);
    setPeriodPreset("CUSTOM");
  };

  // 조회 기간 → 허용 단위. 무효(너무 촘촘) 단위는 disable. 현재 단위가 무효가 되면 자동 스냅(일 우선, 없으면 가장 굵은 단위).
  const allowed = allowedIntervals(periodPreset, customStart, customEnd);
  useEffect(() => {
    if (!allowed.includes(interval)) {
      setInterval(allowed.includes("DAY") ? "DAY" : allowed[allowed.length - 1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodPreset, customStart, customEnd]);

  useEffect(() => {
    if (!metric || manual) {
      setLoading(false);
      return;
    }
    // 직접 지정인데 날짜 미입력이면 대기.
    if (periodPreset === "CUSTOM" && (!customStart || !customEnd)) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await companyKpiUseCase.compute({
          interval,
          periodPreset,
          customStart: periodPreset === "CUSTOM" ? customStart : undefined,
          customEnd: periodPreset === "CUSTOM" ? customEnd : undefined,
          metrics: [
            {
              label: graphSeriesLabel(metric.metricType, metric.label ?? opt?.label),
              sbizType: metric.metricType,
              machineScope: metric.machineScope as KpiMachineScope,
              machineIds: metric.machineIds,
            },
          ],
        });
        if (alive) setData(res);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : "계산 실패");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card, interval, periodPreset, customStart, customEnd, manual]);

  // 실측값 — SUM 지표는 조회 기간 합(없으면 0), AVG 지표는 평균(없으면 목표값 폴백).
  const actual = useMemo<number | null>(() => {
    if (manual) return metric?.manualValue ?? null;
    if (!data) return null;
    const vals = data.series.flatMap((s) => s.points.map((p) => p.value));
    if (aggregate === "SUM") return vals.reduce((a, b) => a + b, 0); // 합, 없으면 0
    if (vals.length) return vals.reduce((a, b) => a + b, 0) / vals.length; // 평균
    return metric?.goalValue ?? null; // 평균인데 자료 없으면 목표값
  }, [manual, metric, data, aggregate]);

  // 실제 데이터가 있는 버킷(점)만 — 0으로 채운 버킷은 드릴다운 제외.
  const realBuckets = useMemo(
    () => new Set((data?.series ?? []).flatMap((s) => s.points.map((p) => p.bucketStart))),
    [data],
  );

  // 그래프에 넘길 응답 — 자료가 전혀 없고 목표값이 있으면 실측 폴백과 동일하게
  // 그래프도 목표값 평탄선으로(0으로 깔리지 않게). 자료 일부라도 있으면 원본 그대로(0 채움).
  const chartResponse = useMemo<ComputeResponse | null>(() => {
    if (!data) return null;
    const goal = metric?.goalValue;
    // 평균 지표만 목표값 평탄선(SUM 지표는 자료 없으면 0이 맞음).
    if (aggregate === "AVG" && realBuckets.size === 0 && goal != null && data.series.length) {
      return {
        ...data,
        series: data.series.map((s) => ({
          ...s,
          points: data.buckets.map((b) => ({ bucketStart: b, value: goal })),
        })),
      };
    }
    return data;
  }, [data, realBuckets, metric, aggregate]);

  const base = metric?.baseValue ?? null;
  const goal = metric?.goalValue ?? null;
  const rate = achievement(base, goal, actual, direction);
  const hasBenchmark = base != null && goal != null;

  return (
    <div className="bg-white border border-[#e0e0e0] rounded-[12px] p-4 flex flex-col">
      <div className="flex items-start justify-between mb-1">
        <div>
          <div className="text-[15px] font-medium text-[#222]">
            {card.title || opt?.label || metric?.metricType || "—"}
          </div>
          {manual && <div className="text-[12px] text-[#888] mt-0.5">수동입력</div>}
        </div>
        <div className="flex gap-2 text-[13px]">
          <button onClick={() => onEdit(card)} className="text-[#4285F4] hover:text-[#3367D6]">
            수정
          </button>
          <button onClick={() => onDelete(card)} className="text-[#888] hover:text-[#F24A4A]">
            삭제
          </button>
        </div>
      </div>

      {/* 벤치마크 행 */}
      {hasBenchmark && (
        <div className="grid grid-cols-4 gap-2 my-2 text-center">
          <Bench label="현재값(도입 전)" value={fmt(base, unit)} />
          <Bench label="실측" value={fmt(actual, unit)} accent />
          <Bench label="목표" value={fmt(goal, unit)} />
          <Bench
            label="달성률"
            value={rate == null ? "—" : `${Number(rate.toFixed(1))}%`}
            accent
          />
        </div>
      )}

      {/* 계산형: 차트 + 즉시조회 칩 / 수동형: 안내 */}
      {manual ? (
        <div className="mt-2 text-[12px] text-[#888] bg-[#FAFAFA] border border-[#eee] rounded-[8px] p-3">
          수동입력 지표입니다. 실측값은 <b>수정</b>에서 직접 갱신하세요.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap my-2">
            <ChipGroup
              options={INTERVALS}
              value={interval}
              onChange={setInterval}
              isDisabled={(v) => !allowed.includes(v)}
            />
            <ChipGroup options={PERIODS} value={periodPreset} onChange={selectPreset} />
            <div className="ml-auto">
              <ChipGroup
                options={[
                  { v: "LINE", l: "선" },
                  { v: "BAR", l: "막대" },
                ]}
                value={chartType}
                onChange={setChartType}
                equalWidth
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mb-2 text-[12px]">
            <input
              type="date"
              value={customStart}
              onChange={(e) => editStart(e.target.value)}
              className="border border-[#e0e0e0] rounded-[6px] px-2 py-1 text-[12px] focus:border-[#4285F4] focus:outline-none"
            />
            <span className="text-[#888]">~</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => editEnd(e.target.value)}
              className="border border-[#e0e0e0] rounded-[6px] px-2 py-1 text-[12px] focus:border-[#4285F4] focus:outline-none"
            />
          </div>
          <div className="flex-1 min-h-[240px] flex flex-col">
            {loading && (
              <div className="flex-1 flex items-center justify-center text-[13px] text-[#888]">
                불러오는 중…
              </div>
            )}
            {error && (
              <div className="flex-1 flex items-center justify-center text-[13px] text-[#F24A4A]">
                {error}
              </div>
            )}
            {!loading && !error && data && (
              <ComputeChart
                response={chartResponse ?? data}
                chartType={chartType}
                height="100%"
                onPointClick={(bucket) => {
                  // 0으로 채운(실데이터 없는) 점은 무시 — 그 날 로그 조회 안 함.
                  // 버킷은 datetime(분/시간) 또는 자정(일+) — 로그 모달은 날짜 단위라 yyyy-MM-dd 로 절단.
                  if (realBuckets.has(bucket)) setDrilldown(bucket.slice(0, 10));
                }}
                onZoom={(startBucket, endBucket) => {
                  // 드래그 구간 → 그 기간으로 CUSTOM 재조회(기존 compute effect + 자동 스냅이 처리).
                  setCustomStart(startBucket.slice(0, 10));
                  setCustomEnd(endBucket.slice(0, 10));
                  setPeriodPreset("CUSTOM");
                }}
              />
            )}
          </div>
        </>
      )}

      {/* 아래: 기간 평균 + 목표 대비 성과 — 현재값/목표값(벤치마크) 설정 시에만 표시 */}
      {actual != null && hasBenchmark && (
        <div className="mt-3 pt-3 border-t border-[#eee] grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-[12px] text-[#555] font-bold mb-1.5">
              {aggregate === "SUM" ? "기간 합계" : "기간 평균"}
            </div>
            <div className="text-[13px] text-[#222]">
              {aggregate === "SUM" ? "합계" : "평균"} {opt?.label ?? "값"}{" "}
              <b className="text-[#4285F4]">{fmt(actual, unit)}</b>
            </div>
          </div>
          <div>
            <div className="text-[12px] text-[#555] font-bold mb-1.5">목표 대비 성과</div>
            <Compare base={base} actual={actual} goal={goal} unit={unit} />
          </div>
        </div>
      )}

      {drilldown && (
        <WorkloadLogModal
          date={drilldown}
          machineIds={cardMachineIds}
          onClose={() => setDrilldown(null)}
        />
      )}
    </div>
  );
};

const Compare = ({
  base,
  actual,
  goal,
  unit,
}: {
  base: number | null;
  actual: number | null;
  goal: number | null;
  unit: string;
}) => {
  const max = Math.max(base ?? 0, actual ?? 0, goal ?? 0) || 1;
  // 값 없는 행(이전/목표)은 생략 — 벤치마크 미사용 시 '현재'만 표시.
  const Row = ({ label, val, color }: { label: string; val: number | null; color: string }) =>
    val == null ? null : (
      <div className="flex items-center gap-2 text-[11px]">
        <span className="w-7 text-[#888] shrink-0">{label}</span>
        <div className="flex-1 bg-[#F0F1F4] rounded h-[10px] overflow-hidden">
          <div
            className="h-[10px] rounded"
            style={{ width: `${(val / max) * 100}%`, background: color }}
          />
        </div>
        <span className="w-14 text-right text-[#555] shrink-0">{fmt(val, unit)}</span>
      </div>
    );
  return (
    <div className="space-y-1.5">
      <Row label="이전" val={base} color="#BBBBBB" />
      <Row label="현재" val={actual} color="#4285F4" />
      <Row label="목표" val={goal} color="#A9C7FF" />
    </div>
  );
};

const Bench = ({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) => (
  <div className="bg-[#FAFAFA] border border-[#eee] rounded-[8px] py-2">
    <div className="text-[11px] text-[#888]">{label}</div>
    <div className={`text-[14px] font-bold ${accent ? "text-[#4285F4]" : "text-[#222]"}`}>
      {value}
    </div>
  </div>
);